import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Star, RefreshCw, ArrowLeft, ArrowRight, Loader2, TrendingUp, MessageSquare } from 'lucide-react';

const fmt = (n) => Number(n).toFixed(1).replace('.', ',');

function Stars({ value, size = 'w-6 h-6' }) {
  const full = Math.floor(value);
  const frac = value - full;
  return (
    <div className="flex gap-0.5 justify-center">
      {[1, 2, 3, 4, 5].map((i) => {
        if (i <= full) return <Star key={i} className={`${size} fill-[hsl(var(--rating-gold))] text-[hsl(var(--rating-gold))]`} />;
        if (i === full + 1 && frac >= 0.25) {
          return (
            <span key={i} className={`relative inline-flex ${size}`}>
              <Star className={`${size} fill-gray-200 text-gray-200 absolute inset-0`} />
              <Star className={`${size} fill-[hsl(var(--rating-gold))] text-[hsl(var(--rating-gold))] absolute inset-0`}
                    style={{ clipPath: `inset(0 ${Math.round((1 - frac) * 100)}% 0 0)` }} />
            </span>
          );
        }
        return <Star key={i} className={`${size} fill-gray-200 text-gray-200`} />;
      })}
    </div>
  );
}

/*
 * "Spiegel"-Schritt (Feedback 04.09., 50:59 / 54:28 / 62:15):
 * Erst hier wird der Durchschnitt gezeigt — IMMER, auch wenn er schlechter ist.
 * Darunter der KI-Textvorschlag (bearbeitbar) und die Weiche.
 *   converted  → weiter in den positiven Abschluss (Bewertung übertragen)
 *   sonst      → "Erst reagieren lassen" (intern) oder "Trotzdem absenden" (öffentlich)
 */
export default function MirrorStep({
  impulse, refinedAvg, converted,
  suggestion, setSuggestion, onRegenerate, regenerating,
  onContinuePositive, onChoice, submitting, onBack,
}) {
  const delta = refinedAvg - impulse;
  const improved = delta >= 0.25;
  const worsened = delta <= -0.25;

  return (
    <motion.div
      key="mirror"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <Card className="card-shadow">
        <CardContent className="pt-6 pb-6 space-y-5">
          {/* Durchschnitt */}
          <div className="text-center space-y-2">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Ihre Durchschnittsbewertung</p>
            <Stars value={refinedAvg} size="w-7 h-7" />
            <p className="text-3xl font-bold leading-none">
              {fmt(refinedAvg)}<span className="text-base text-muted-foreground font-normal"> von 5</span>
            </p>
            {improved && (
              <p className="text-sm text-green-700 inline-flex items-center gap-1.5 justify-center">
                <TrendingUp className="w-4 h-4" />
                Im Detail fällt Ihre Bewertung besser aus als der erste Eindruck ({impulse} Sterne).
              </p>
            )}
            {worsened && (
              <p className="text-sm text-muted-foreground">
                Im Detail fällt Ihre Bewertung etwas niedriger aus als der erste Eindruck ({impulse} Sterne).
              </p>
            )}
            {!improved && !worsened && (
              <p className="text-sm text-muted-foreground">Das deckt sich mit Ihrem ersten Eindruck.</p>
            )}
          </div>

          {/* Textvorschlag */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium">Ihr Textvorschlag</label>
              <span className="text-[11px] text-muted-foreground">Auf Basis Ihrer Detailbewertung erstellt</span>
            </div>
            <div className="relative">
              <Textarea
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                className="pr-12 text-sm leading-relaxed resize-none min-h-[150px]"
                onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                data-testid="mirror-suggestion-textarea"
              />
              <Button size="icon" variant="ghost" onClick={onRegenerate} disabled={regenerating}
                      className="absolute top-2 right-2" title="Neuen Vorschlag generieren">
                <RefreshCw className={`w-4 h-4 ${regenerating ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Sie können den Text frei anpassen.</p>
          </div>

          {/* Weiche */}
          {converted ? (
            <Button onClick={onContinuePositive} disabled={submitting} className="w-full h-11" data-testid="mirror-continue-positive">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Weiter zur Bewertung
            </Button>
          ) : (
            <div className="space-y-2 pt-1">
              <p className="text-sm font-medium">Wie möchten Sie fortfahren?</p>
              <Button variant="secondary" className="w-full justify-start h-auto py-3.5"
                      onClick={() => onChoice('send_to_hv')} disabled={submitting} data-testid="reaction-gate-send-to-hv-button">
                <MessageSquare className="w-4 h-4 mr-3 shrink-0 text-accent" />
                <div className="text-left">
                  <p className="font-medium">Erst reagieren lassen</p>
                  <p className="text-xs text-muted-foreground font-normal">Die Hausverwaltung erhält Ihr Feedback intern und kann darauf eingehen.</p>
                </div>
              </Button>
              <Button variant="outline" className="w-full justify-start h-auto py-3.5"
                      onClick={() => onChoice('submit_now')} disabled={submitting} data-testid="reaction-gate-confirm-button">
                <ArrowRight className="w-4 h-4 mr-3 shrink-0" />
                <div className="text-left">
                  <p className="font-medium">Trotzdem öffentlich bewerten</p>
                  <p className="text-xs text-muted-foreground font-normal">Ihr Text wird in den nächsten Schritt übernommen.</p>
                </div>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Button variant="ghost" onClick={onBack} className="w-full text-muted-foreground">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Zurück zur Detailbewertung
      </Button>
    </motion.div>
  );
}
